"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
  useCallback,
} from "react";
import { UserRoleEnum, type UserInfo } from "~/lib/objects";
import { authClient } from "~/utils/auth-client";

export interface UserInfoResolutionFailure {
  kind: "denied" | "recoverable";
  status?: number;
  // Ephemeral attribution to this resolution; never persist either token.
  episode: object;
  lifetime: object;
}

interface UserInfoContextValue {
  userInfo?: UserInfo;
  refetchUserInfo: () => void;
  invalidateUserInfo: () => void;
  resolutionStatus: "pending" | "anonymous" | "resolved" | "error";
  // Transient identity only: never persist this or use it in a cache key.
  resolutionLifetime: object;
  resolutionFailure?: UserInfoResolutionFailure;
}

const UserInfoContext = createContext<UserInfoContextValue | undefined>(
  undefined,
);

interface UserInfoProviderProps {
  children: ReactNode;
}

export const UserInfoProvider: React.FC<UserInfoProviderProps> = ({
  children,
}) => {
  const sessionState = authClient.useSession();
  const { data: session, error } = sessionState;
  // better-auth 1.2 exposes this at runtime but omits it from its React declaration.
  const isPending =
    sessionState.isPending ||
    ("isRefetching" in sessionState && sessionState.isRefetching === true);
  const sessionKey =
    !isPending && !error && session
      ? JSON.stringify([session.session.id, session.user.id])
      : null;
  const [identity, setIdentity] = useState({ sessionKey, lifetime: {} });
  // Adjust before children render: a temporary refresh does not confirm departure.
  const authorizationFailed = error?.status === 401 || error?.status === 403;
  if (
    !isPending &&
    (!error || authorizationFailed) &&
    identity.sessionKey !== sessionKey
  ) {
    setIdentity({ sessionKey, lifetime: {} });
  }
  const [resolved, setResolved] = useState<{
    sessionKey: string | null;
    userInfo: UserInfo;
  }>();
  const [failedSession, setFailedSession] = useState<string | null>();
  const [resolutionFailure, setResolutionFailure] = useState<UserInfoResolutionFailure>();
  const lifetimeRef = useRef(identity.lifetime);
  lifetimeRef.current = identity.lifetime;
  const [observedKey, setObservedKey] = useState(sessionKey);
  if (observedKey !== sessionKey) {
    setObservedKey(sessionKey);
    setResolved(undefined);
    setFailedSession(undefined);
    setResolutionFailure(undefined);
  }
  const [invalidated, setInvalidated] = useState(false);
  const requestId = useRef(0);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const invalidatePendingRequests = useCallback(() => {
    requestId.current++;
  }, []);
  const advanceLifetime = useCallback(() => {
    const lifetime = {};
    setIdentity((current) => ({ ...current, lifetime }));
    return lifetime;
  }, []);
  const invalidateUserInfo = useCallback(() => {
    invalidatePendingRequests();
    setResolved(undefined);
    setInvalidated(true);
    setResolutionFailure(undefined);
    advanceLifetime();
  }, [advanceLifetime, invalidatePendingRequests]);
  const refetchUserInfo = useCallback(() => {
    invalidatePendingRequests();
    setResolved(undefined);
    setFailedSession(undefined);
    setResolutionFailure(undefined);
    setRefreshVersion((version) => version + 1);
  }, [invalidatePendingRequests]);

  const fetchUserData = useCallback(async () => {
    const id = ++requestId.current;
    setInvalidated(false);
    setResolved(undefined);
    setResolutionFailure(undefined);
    try {
      const response = await fetch("/api/user-info");
      if (id !== requestId.current) return;
      if (!response.ok) {
        setFailedSession(sessionKey);
        const denied = response.status === 401 || response.status === 403;
        const lifetime = denied ? advanceLifetime() : lifetimeRef.current;
        setResolutionFailure({ kind: denied ? "denied" : "recoverable", status: response.status, episode: {}, lifetime });
        return;
      }
      const data = (await response.json()) as UserInfo;
      if (id === requestId.current) {
        setResolved({ sessionKey, userInfo: data });
        if (
          data.role !== UserRoleEnum.ADMIN &&
          data.role !== UserRoleEnum.STUDENT
        ) {
          advanceLifetime();
        }
      }
    } catch {
      if (id === requestId.current) {
        setFailedSession(sessionKey);
        setResolutionFailure({ kind: "recoverable", episode: {}, lifetime: lifetimeRef.current });
      }
    }
  }, [sessionKey, advanceLifetime]);

  useEffect(() => {
    if (sessionKey) void fetchUserData();
    return invalidatePendingRequests;
  }, [fetchUserData, sessionKey, refreshVersion, invalidatePendingRequests]);

  const userInfo =
    sessionKey && resolved?.sessionKey === sessionKey
      ? resolved.userInfo
      : undefined;
  const contextValue: UserInfoContextValue = {
    userInfo,
    refetchUserInfo,
    invalidateUserInfo,
    resolutionStatus: isPending
      ? "pending"
      : error
        ? "error"
        : !sessionKey
          ? "anonymous"
          : userInfo
            ? "resolved"
            : invalidated || failedSession === sessionKey
              ? "error"
              : "pending",
    resolutionLifetime: identity.lifetime,
    resolutionFailure: resolutionFailure?.lifetime === identity.lifetime ? resolutionFailure : undefined,
  };

  return (
    <UserInfoContext.Provider value={contextValue}>
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = (): UserInfoContextValue => {
  const context = useContext(UserInfoContext);
  if (context === undefined) {
    throw new Error("useUserInfo must be used within a UserInfoProvider");
  }
  return context;
};
